import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { AuthorizationStates } from '@racemap/utilities/consts/events';
import {
  getBasePriceOfEventType,
  getStripeProductOfBillableItemType,
} from '@racemap/utilities/functions/billing';
import { isEmptyString, sleep } from '@racemap/utilities/functions/utils';
import type { User_Legacy } from '@racemap/utilities/types/users';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Button } from 'antd';
import type { Immutable } from 'immer';
import { useEffect, useState } from 'react';
import { useUser } from 'src/lib/customHooks';
import { useGetProductPricePerUnit } from 'src/lib/customHooks/useGetProductPricePerUnit';
import { sendAnalyticsEvent } from 'src/lib/googleAnalytics';
import { getLabelForBillingItemType } from 'src/misc/getLabelForBillingItemType';
import { useInterval } from 'usehooks-ts';
import type { CurrentEvent } from '../../../store/events/events_reducers';
import { useStore } from '../../../store/reducers';
import { LoadingIndicator } from '../../BasicComponents/LoadingIndicator/index';
import { IconArrowLeft, IconOk } from '../../Icon';
import type { ViewSlots } from './useOverviewView';

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const publicKey =
  window.location.origin !== 'https://racemap.com'
    ? 'pk_test_a5YN3NS4wfpVtSwHbOx67CEb00xRzNlVCW'
    : 'pk_live_IHKLCoVN3GEWqkTfBijqv1Kh00MhcgtToA';
const stripePromise = loadStripe(publicKey);

interface Args {
  user: Immutable<User_Legacy>;
  event: Immutable<CurrentEvent>;
  enabled: boolean;
  onBack: () => void;
  onCancel: () => void;
  onNext: () => void;
}

export const useEmbeddedStripeView = ({
  event,
  enabled,
  onCancel,
  onBack,
  onNext,
}: Args): ViewSlots => {
  const { user: creator } = useUser({ userId: event.creatorId });
  const [clientSecret, setClientSecret] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [sessionState, setSessionState] = useState<'complete' | 'open' | 'unknown'>('unknown');
  const [error, setError] = useState('');
  const getPricePerUnit = useGetProductPricePerUnit({ isReseller: creator?.isReseller });
  const { loadCurrentEvent } = useStore((s) => s.events.actions);
  const isSessionComplete = sessionState === 'complete';
  const isEventPaid = event.authorization === AuthorizationStates.PAID;
  const basePrice = getBasePriceOfEventType(event.type);
  const stripeBasePrice = basePrice ? getStripeProductOfBillableItemType(basePrice) : null;

  const createCheckoutSession = async () => {
    setError('');
    const state = await checkSessionState();
    if (state === 'complete') return;

    const response = await fetch('/api/billing/action/create_checkout_session', {
      method: 'POST',
      body: JSON.stringify({ eventId: event.id, returnTo: window.location.pathname }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      setError('Failed to create checkout session');
    }

    const data = await response.json();
    setClientSecret(data.clientSecret);
    setSessionId(data.sessionId);
  };

  const checkSessionState = async (): Promise<'open' | 'complete'> => {
    if (isEmptyString(sessionId)) return 'open';
    const response = await fetch(
      `/api/billing/action/check_session_state?sessionId=${sessionId}&eventId=${event.id}`,
    );
    const data = await response.json();
    setSessionState(data.status);

    return data.status;
  };

  const handleSessionComplete = async () => {
    if (stripeBasePrice != null) {
      const price = getPricePerUnit?.(stripeBasePrice, 1);

      sendAnalyticsEvent('purchase', {
        value: price?.amount || 0,
        currency: 'EUR',
        transaction_id: sessionId,
        items: [
          {
            item_id: stripeBasePrice,
            item_name: getLabelForBillingItemType(stripeBasePrice),
          },
        ],
      });
    }

    await sleep(2000);
    await checkSessionState();
  };

  useEffect(() => {
    if (!enabled) return;
    createCheckoutSession();
  }, [enabled]);

  useInterval(
    () => {
      checkSessionState();
    },
    enabled && sessionState !== 'complete' ? 5000 : null,
  );

  useInterval(
    () => {
      if (sessionState === 'complete') {
        loadCurrentEvent(event.id);
      }
    },
    enabled && event.authorization !== 'PAID' ? 10000 : null,
  );

  return {
    title: '2. Pay base price',
    body: (
      <ModalBody>
        {clientSecret ? (
          <EmbeddedCheckoutProvider
            stripe={stripePromise}
            options={{ clientSecret, onComplete: handleSessionComplete }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        ) : error ? (
          <ErrorContainer>
            {error}
            <Button onClick={createCheckoutSession}>Retry</Button>
          </ErrorContainer>
        ) : (
          <LoadingContainer>
            <LoadingIndicator borderless />
          </LoadingContainer>
        )}
      </ModalBody>
    ),
    footer: (
      <>
        <Button onClick={onCancel}>Cancel</Button>
        {!isSessionComplete || !isEventPaid ? (
          <Button onClick={onBack} icon={<IconArrowLeft />} type="default">
            Back
          </Button>
        ) : !isEventPaid ? (
          <Button loading type="primary">
            Processing
          </Button>
        ) : (
          <Button onClick={onNext} icon={<IconOk />} type="primary">
            Complete
          </Button>
        )}
      </>
    ),
  };
};

const ModalBody = styled.div`
  width: 450px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: 500px;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  height: 100%;
  min-height: 500px;
  font-size: 1.3em;
  color: ${RacemapColors.DarkGray};
  gap: 10px;
`;
